import React from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
} from "reactstrap";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import { connect } from "react-redux";
import loadingAction from "../../core/redux/actions/loadingAction";
import ReactTable from "react-table";
import TablePagination from "components/Table/TablePagination";
import strings from "../../core/translate";
import RowActions, {
  editAction,
  deleteAction,
} from "components/Table/RowActions";
import Constants from "../../core/Constants";
import SelectRow from "../../components/Select/SelectRow";
import { Scopes } from "../../shared/scopes.enum";
import GarageSectionHeader from "../../components/Garage/GarageSectionHeader";
import { FormModal } from "../../components/Form/FormKit";
import BranchServiceForm from "./BranchServiceForm";

class MerchantBranchServiceList extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      dataSource: [],
      merchants: [],
      branches: [],
      selectedMerchant: "",
      selectedBranch: "",
      branchName: "",
      globalFilter: "",
      startDateFilter: null,
      endDateFilter: null,
      canWrite: false,
      formOpen: false,
      editing: null,
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    const merchantIdFromStorage = sessionStorage.getItem("merchantId");
    const routeState = this.props.location.state || {};
    const { selectedBranch } = this.props.match.params;
    // opened from a garage record, so the garage is fixed for this page
    const branchId =
      selectedBranch && selectedBranch !== ":selectedBranch"
        ? selectedBranch
        : "";
    const selectedMerchant =
      merchantIdFromStorage || routeState.selectedMerchant || "";

    this.setState(
      {
        canWrite: this.hasPermission([
          Scopes.MERCHANTS_BRANCHES_SERVICES_WRITE,
        ]),
        selectedMerchant,
        selectedBranch: branchId,
        branchName: routeState.branchName || "",
      },
      () => {
        if (selectedMerchant) {
          this.fetchBranches();
        } else {
          this.fetchMerchants();
        }
        if (branchId) {
          this.callPage();
        } else {
          this.setState({ isLoading: false });
        }
      }
    );
  }

  // Fetch merchants list
  fetchMerchants = async () => {
    const response = await this.engine.getItem("merchants");
    if (response && response.status === 200) {
      this.setState({
        isLoading: false,
        merchants: response.data.items.map((merchant) => ({
          value: merchant.id,
          label: merchant.name,
        })),
      });
      if (this.state.merchants.length === 1) {
        this.setState({
          selectedMerchant: this.state.merchants[0].value,
        });
        this.fetchBranches();
      }
    }
  };

  // Fetch branches based on the selected merchant
  fetchBranches = async () => {
    const { selectedMerchant } = this.state;

    if (!selectedMerchant) {
      console.error("Merchant ID not found in sessionStorage");
      return;
    }

    const response = await this.engine.getItem(
      `merchants/${selectedMerchant}/branches`
    );
    if (response && response.status === 200) {
      const branches = response.data.items.map((branch) => ({
        value: branch.id,
        label: branch.name || branch.address,
      }));
      this.setState((prev) => ({
        isLoading: false,
        branches,
        branchName: prev.branchName || this.findBranchName(branches, prev),
      }));
    }
  };

  findBranchName(branches, state) {
    const match = branches.find((branch) => branch.value === state.selectedBranch);
    return match ? match.label : "";
  }

  // Fetch services based on selected branch
  async callPage() {
    const { selectedBranch } = this.state;
    if (!selectedBranch || selectedBranch === ":selectedBranch") return;

    const pathname = `merchants-branches/${selectedBranch}/services`;
    const response = await this.engine.getItem(pathname);
    if (response && response.status === 200) {
      this.setState({
        isLoading: false,
        dataSource: response.data.items.map((item, key) => ({
          id: key,
          data: item,
          // a deleted service comes back as null; the row stays so it can be removed
          serviceName: item.service?.name ?? "-",
          servicePhoto: item.service?.iconUrl ? (
            <img
              alt={item.service.name}
              key={key}
              src={`${Constants.serverlink}api/v1/services/${item.service.iconUrl}`}
              className={"imgsponser"}
            />
          ) : (
            "-"
          ),
          merchantName: item.branch.merchant.name,
          branchAddress: item.branch.address,
          created: new Date(item.createdAt),
          actions: this.handleActions(key, item),
        })),
      });
    }
    this.props.loadingAction(false);
  }

  async handleDelete(valueid) {
    if (window.confirm(strings.confirmdelete)) {
      this.props.loadingAction(true);
      const { selectedBranch } = this.state;
      const result = await this.engine.deleteItem(
        `merchants-branches/${selectedBranch}/services`,
        valueid
      );
      if (result) {
        this.handlePage();
      }
      this.props.loadingAction(false);
    }
  }

  handlePage() {
    this.props.loadingAction(true);
    this.callPage();
  }
  // add and edit open the same dialog over the list
  handleEdit(index) {
    this.setState({
      formOpen: true,
      editing: this.state.dataSource[index].data,
    });
  }

  handleCloseForm = () => this.setState({ formOpen: false, editing: null });

  handleSaved = () => {
    this.setState({ formOpen: false, editing: null }, this.handlePage);
  };

  // Opens the price list / details of one service of this garage
  handleOpenDetails = (item) => {
    const { selectedMerchant, selectedBranch, branchName } = this.state;
    this.props.history.push({
      pathname: `/admin/merchants-branches/${selectedBranch}/services/${item.id}/details`,
      state: { selectedMerchant, selectedBranch, branchName },
    });
  };

  // Actions dropdown
  handleActions = (index, item) => (
    <RowActions
      actions={[
        {
          key: "details",
          icon: "fa fa-list-ul",
          label: strings.branch_service_details,
          onClick: () => this.handleOpenDetails(item),
        },
        editAction(() => this.handleEdit(index, item.id), this.state.canWrite),
        deleteAction(() => this.handleDelete(item.id), this.state.canWrite),
      ]}
    />
  );

  // Handle merchant selection change
  handleMerchantSelect = (event) => {
    const selectedMerchant = event.value;

    if (selectedMerchant !== this.state.selectedMerchant) {
      this.setState(
        { selectedMerchant, selectedBranch: "", branches: [], dataSource: [] },
        () => {
          this.fetchBranches();
        }
      );
    }
  };

  // Handle branch selection change
  handleBranchSelect = (event) => {
    const selectedBranch = event.value;
    this.setState(
      {
        selectedBranch,
        branchName: this.findBranchName(this.state.branches, { selectedBranch }),
      },
      () => {
        this.props.history.push(
          `/admin/merchants-branches/${selectedBranch}/services`
        );
        this.callPage();
      }
    );
  };

  // Handle adding a new service
  handleAdd = () => {
    const { selectedBranch } = this.state;

    if (!selectedBranch || selectedBranch === ":selectedBranch") {
      alert(strings.select + " " + strings.garage);
      return;
    }
    this.setState({ formOpen: true, editing: null });
  };

  render() {
    const {
      dataSource,
      isLoading,
      merchants,
      branches,
      selectedMerchant,
      selectedBranch,
      branchName,
      canWrite,
      formOpen,
      editing,
    } = this.state;
    const isGarageScoped = Boolean(selectedBranch);

    let columns = [
      { Header: `${strings.service} ${strings.name}`, accessor: "serviceName" },
      {
        Header: `${strings.service} ${strings.picture}`,
        accessor: "servicePhoto",
      },
      {
        Header: `${strings.merchant} ${strings.name}`,
        accessor: "merchantName",
      },
      {
        Header: strings.created,
        accessor: "created",
        Cell: (row) => row.value.toLocaleDateString(),
      },
      {
        Header: strings.actions,
        accessor: "actions",
        sortable: false,
        filterable: false,
      },
    ];

    return (
      <div className="content">
        <GarageSectionHeader
          branchId={selectedBranch}
          branchName={branchName}
          merchantId={selectedMerchant}
          active="services"
          history={this.props.history}
        />
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4" className="pull-left">
                  {isGarageScoped ? strings.services : ""}
                </CardTitle>
                {canWrite && (
                  <Button
                    color="primary"
                    className="pull-right"
                    onClick={this.handleAdd}
                  >
                    {strings.add}
                  </Button>
                )}

                {/* Garage pickers only when the page was not opened from a garage */}
                {!isGarageScoped &&
                  !sessionStorage.getItem("merchantId") &&
                  merchants.length !== 1 && (
                    <SelectRow
                      label={strings.merchant}
                      name="selectedMerchant"
                      defaultValue={selectedMerchant}
                      data={merchants}
                      changeInput={this.handleMerchantSelect}
                      placeholder={strings.select}
                    />
                  )}

                {!isGarageScoped && (
                  <SelectRow
                    key={selectedMerchant || "default"}
                    label={strings.garage}
                    name="selectedBranch"
                    defaultValue={selectedBranch}
                    data={branches}
                    changeInput={this.handleBranchSelect}
                    placeholder={strings.select}
                    viewonly={!selectedMerchant}
                  />
                )}
              </CardHeader>
              <CardBody>
                <ReactTable
                  previousText={strings.previous}
                  nextText={strings.next}
                  loadingText={strings.loading}
                  noDataText={strings.nodata}
                  pageText={strings.page}
                  data={dataSource}
                  defaultFilterMethod={this.filterCaseInsensitive}
                  filterable
                  loading={isLoading}
                  columns={columns}
                  defaultPageSize={10}
                  showPaginationTop={false}
                  showPaginationBottom
                  PaginationComponent={TablePagination}
                  className="-striped -highlight primary-pagination"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <FormModal
          isOpen={formOpen}
          onClose={this.handleCloseForm}
          icon="nc-icon nc-settings"
          size="md"
          title={
            editing
              ? `${strings.edit} ${strings.service}`
              : `${strings.add} ${strings.service}`
          }
          subtitle={branchName}
        >
          <BranchServiceForm
            key={(editing && editing.id) || "new"}
            item={editing}
            branchId={selectedBranch}
            onSaved={this.handleSaved}
            onCancel={this.handleCloseForm}
          />
        </FormModal>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(MerchantBranchServiceList);
